import { useMemo, createContext, useContext } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { useLoginErrorCode } from '@/bo-kit/hooks/useLoginErrorCode';
import { useTranslation } from 'react-i18next';
import { LoginAndResetPassErrorCode } from '../../../../../../transport/types/LoginAndResetPassErrorCode';
import { SendCodeRequest } from '../../../../../../transport/types/SendCodeRequest';

type UseDoResetPasswordSendCode = [
    (data: SendCodeRequest) => void,
    {
        isSuccess: boolean;
        isLoading: boolean;
        isError: boolean;
        error?: FetchBaseQueryError | SerializedError;
        reset: () => void;
    }
];

export const UseDoResetPasswordSendCodeMutationContext = createContext<
    UseDoResetPasswordSendCode | undefined
>(undefined);

export const useDoResetPasswordSendCode = () => {
    const { t } = useTranslation('bo-kit__signin');
    const contextValue = useContext(UseDoResetPasswordSendCodeMutationContext);
    if (!contextValue) {
        throw new Error(
            "No useDoVerifyResetPassword defined with react context, pls implement: '<UseDoResetPasswordMutationContext.Provider value={[doResetPassword, state]}>{children}</UseDoResetPasswordMutationContext.Provider>'"
        );
    }

    const [doSendCode, state] = contextValue;

    const { errorCode, roles } = useLoginErrorCode(state);

    const errorMessage = useMemo<string | null>(() => {
        if (LoginAndResetPassErrorCode.ROLECONFLICT === errorCode) {
            return null;
        }
        if (LoginAndResetPassErrorCode.USERNOTFOUND === errorCode) {
            return t('No such user');
        }
        if (!state.isError) {
            return null;
        }
        if (!state.error || !('error' in state.error)) {
            return t('An unexpected error occurred');
        }
        return state.error.error ?? null;
    }, [state.error, state.isError, errorCode, t]);

    return {
        doSendCode,
        ...state,
        roles,
        isError: errorCode != null,
        error: errorCode,
        errorMessage,
    };
};
